import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, User, Briefcase, Code, Award, Mail, Layers } from 'lucide-react';

const links = [
  { id: 'hero',           label: 'Home',        icon: Home },
  { id: 'about',          label: 'About',       icon: User },
  { id: 'skills',         label: 'Skills',      icon: Layers },
  { id: 'experience',     label: 'Experience',  icon: Briefcase },
  { id: 'projects',       label: 'Projects',    icon: Code },
  { id: 'certifications', label: 'Certs',       icon: Award },
  { id: 'contact',        label: 'Contact',     icon: Mail },
];

const Navbar = () => {
  const [hovered, setHovered]   = useState(null);
  const [active,  setActive]    = useState('hero');

  // Track which section is in view
  useEffect(() => {
    const ids = links.map((l) => l.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
  };

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[200]">
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22, delay: 0.4 }}
        className="flex items-center gap-1 px-3 py-2 rounded-2xl bg-black/70 backdrop-blur-2xl border border-white/10 shadow-2xl shadow-black/40"
      >
        {links.map((link) => {
          const Icon      = link.icon;
          const isHovered = hovered === link.id;
          const isActive  = active  === link.id;

          return (
            <div
              key={link.id}
              className="relative flex items-center justify-center"
              onMouseEnter={() => setHovered(link.id)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Hover / active background pill */}
              {(isHovered || isActive) && (
                <motion.div
                  layoutId="nav-pill"
                  transition={{ type: 'spring', bounce: 0.25, duration: 0.4 }}
                  className={`absolute inset-0 rounded-xl ${isActive ? 'bg-primary/20' : 'bg-white/8'}`}
                />
              )}

              {/* Tooltip */}
              <AnimatePresence>
                {isHovered && (
                  <motion.div
                    initial={{ opacity: 0, y: -6, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.9 }}
                    transition={{ duration: 0.15 }}
                    className="absolute -bottom-10 px-2.5 py-1 bg-black/90 border border-white/10 text-white text-[10px] mono uppercase tracking-widest rounded-lg whitespace-nowrap pointer-events-none"
                  >
                    {link.label}
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                onClick={() => scrollTo(link.id)}
                aria-label={link.label}
                className={`relative w-11 h-11 flex items-center justify-center rounded-xl transition-colors duration-200 ${
                  isActive ? 'text-primary' : isHovered ? 'text-white' : 'text-slate-500'
                }`}
              >
                <Icon size={18} strokeWidth={1.6} />
                {/* Active dot indicator */}
                {isActive && (
                  <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                )}
              </button>
            </div>
          );
        })}
      </motion.nav>
    </div>
  );
};

export default Navbar;
